import React, { useEffect } from "react";

import { Badge, Box, Flex, Text } from "@chakra-ui/react";

import useStore from "../../../../../store";
import { SectionFragment } from "../../../../../graphql/generated/definitions";
import useElementInView from "../../../../../hooks/useElementInView";
import useTranslations from "../../../../../hooks/useTranslations";

type SectionTitleProps = {
  section: Pick<
    SectionFragment,
    "id" | "title" | "description" | "order" | "questions"
  >;
  sectionIndex: number;
};

const SectionTitle: React.FC<SectionTitleProps> = ({
  section,
  sectionIndex,
}) => {
  const t = useTranslations();
  const setCurrentSectionState = useStore(
    (state) => state.report.setCurrentSectionState
  );
  const [ref, visibility] = useElementInView<HTMLDivElement>({
    rootMargin: "0px",
  });
  useEffect(() => {
    if (visibility === "visible") setCurrentSectionState(section.id, false);
  }, [visibility, section.id, setCurrentSectionState]);

  return (
    <Box>
      <Flex
        key={section.id}
        id={section.id}
        ref={ref}
        bgColor="darkBrand"
        py="4"
        px="6"
        mx={["-6", "0"]}
        rounded={["none", "lg"]}
        alignItems={["start", "center"]}
        justify={["center", "start"]}
        direction={["column", "row"]}
        sx={{ scrollMarginTop: 180 }}
      >
        <Flex experimental_spaceX="4" justify="center" alignItems="center">
          <Flex
            h="10"
            w="10"
            shrink={0}
            rounded="full"
            bgColor="white"
            justify="center"
            alignItems="center"
          >
            <Text fontSize="md" fontWeight="semibold">
              {sectionIndex + 1}
            </Text>
          </Flex>
          <Text
            fontSize="xl"
            fontWeight="semibold"
            color="white"
            id={`${section.id}.title`}
          >
            {section.title}
          </Text>
        </Flex>

        <Box pl={["0", "16"]} pt={["2", "0"]}>
          <Badge bgColor="white" color="darkBrand" variant="solid">
            {t("templates.questionsNumber", {
              number: section.questions.length,
            })}
          </Badge>
        </Box>
      </Flex>
      {section.description && (
        <Text
          fontSize="md"
          fontWeight="normal"
          color="gray.600"
          pt="3"
          whiteSpace="pre-line"
          id={`${section.id}.description`}
        >
          {section.description}
        </Text>
      )}
    </Box>
  );
};

export default React.memo(SectionTitle);
